import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView, useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import { Table } from '@mui/material'
import { Skeleton, Tag, Toast, Typography, TableRow } from '@chilecompra/react-kit/components'
import { PUBLIC_URL } from '../../config/settings/environment'
import { onGetBasicBuyerInformationThunk } from './BasicBuyerInformationSection.actions'
import { Box, Card, Column, NotFoundImg, Strong, TableCell } from './BasicBuyerInformationSection.styles'

/**
 * The buyer general information section container.
 */
const BuyerGeneralInformation = () => {
  const dispatch = useDispatch()
  const dni = usePathParam('rut')

  const { error, loading, orgLegalName, orgAddress, email, phone, webSite, sector } = useSelector(
    state => state.basicBuyerInformation
  )

  useEffectOnce(() => {
    dispatch(onGetBasicBuyerInformationThunk(dni))
  }, [dni, dispatch])

  useAnalyticsPageView('/ficha/')

  return (
    <>
      {error && error.reason === 'DNI_DOES_NOT_MATCH_ERROR' && !loading && (
        <Column margin="14px 0 2rem">
          <Card padding="70px" textAlign="center">
            <NotFoundImg alt="Sin información" src={`${PUBLIC_URL}/assets/images/notFound.svg`} />
            <Typography variant="h4" fontWeight="bold" margin="8px 0" tabIndex="0">
              No se encontraron resultados para tu búsqueda
            </Typography>
            <Typography variant="body1" color="black3" fontSize="14px" lineHeight="18px" tabIndex="0">
              El comprador que buscas no se encuentra inscrito en
              <br />
              Mercado Público. Intenta con otra palabra clave u
              <br />
              otro criterio de búsqueda.
            </Typography>
          </Card>
        </Column>
      )}

      {error && error.reason !== 'DNI_DOES_NOT_MATCH_ERROR' && !loading && (
        <Column margin="14px 0 2rem">
          <Toast
            border
            severity="error"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
            tabIndex="0"
          >
            {error.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
              <>
                En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos
                minutos más.
              </>
            )}
            {error.reason === 'UNAUTHORIZED_ERROR' && 'No esta autenticado.'}
          </Toast>
        </Column>
      )}

      {!error && (
        <Column margin="0 0 32px">
          <Box display="flex" gap="8px" flexDirection="column" margin="0 0 24px">
            <Box display="flex">
              {loading && <Skeleton height="35px" variant="rectangular" width="20%" />}
              {!loading && <Tag color="primary" enableIcon={false} text={sector} tabIndex="0" />}
            </Box>

            {loading ? (
              <Skeleton height="30px" variant="rectangular" width="100%" />
            ) : (
              <Typography
                color="black1"
                variant="h1"
                fontWeight="bold"
                fontSize="34px"
                lineHeight="38px"
                align="left"
                tabIndex="0"
              >
                {orgLegalName}
              </Typography>
            )}

            <Typography
              color="black3"
              variant="h3"
              fontWeight="regular"
              fontSize="22px"
              lineHeight="26px"
              align="left"
              tabIndex="0"
            >
              {loading ? <Skeleton height="35px" variant="rectangular" width="25%" /> : 'RUT ' + dni}
            </Typography>
          </Box>
          {loading ? (
            <Skeleton height="272px" variant="rectangular" width="100%" />
          ) : (
            <Card color="default" padding="16px 20px 8px 20px">
              <Box display="flex" flexWrap="wrap" flexDirection="column" justifyContent="space-between">
                <Box>
                  <Typography
                    color="black1"
                    variant="h4"
                    fontWeight="bold"
                    fontSize="18px"
                    lineHeight="22px"
                    tabIndex="0"
                  >
                    Información de la institución
                  </Typography>
                </Box>
                <Box margin="0 0 4px 0">
                  <Typography
                    color="black3"
                    display="inline"
                    fontWeight="regular"
                    fontSize="14px"
                    variant="subtitle"
                    lineHeight="18px"
                    tabIndex="0"
                  >
                    <Strong>Fuente:</Strong> Información declarada por el organismo público en Mercado Público.
                  </Typography>
                </Box>
              </Box>

              <Table accessibility={{ label: 'Información de la institución' }}>
                <TableRow>
                  <TableCell enableHead width="120px" tabIndex="0">
                    Domicilio legal
                  </TableCell>
                  <TableCell tabIndex="0">{orgAddress}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell enableHead width="120px" tabIndex="0">
                    Teléfono
                  </TableCell>
                  <TableCell tabIndex="0">{phone}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell enableHead width="120px" tabIndex="0">
                    Sitio web
                  </TableCell>
                  <TableCell tabIndex="0">{webSite}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell disableBottomBorder enableHead width="120px" tabIndex="0">
                    Correo electrónico
                  </TableCell>
                  <TableCell disableBottomBorder tabIndex="0">
                    {email}
                  </TableCell>
                </TableRow>
              </Table>
            </Card>
          )}
        </Column>
      )}
    </>
  )
}

export default BuyerGeneralInformation
